/**
 * Admin Session Manager Component
 *
 * Allows administrators to view and manage user sessions.
 * Features:
 * - View all active sessions
 * - Filter by user, status, login method
 * - Revoke individual sessions
 * - Force logout all sessions for a user
 * - Session statistics dashboard
 */

import React, { useState, useEffect, useCallback } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'

interface Session {
  session_id: string
  session_id_short: string
  user_id: number
  user_email: string
  user_full_name?: string
  user_username?: string
  user_role?: string
  ip_address: string
  user_agent: string
  login_method: string
  telegram_id?: number
  created_at: string
  last_activity: string
  expires_at: string
  is_active: boolean
  is_expired: boolean
  invalidation_reason?: string
}

interface SessionStats {
  totalSessions: number
  activeSessions: number
  expiredSessions: number
  uniqueActiveUsers: number
  recentSessions: number
  loginMethodCounts: Record<string, number>
}

interface SessionManagerProps {
  currentUser?: any
}

export const SessionManager: React.FC<SessionManagerProps> = () => {
  const [sessions, setSessions] = useState<Session[]>([])
  const [stats, setStats] = useState<SessionStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active')
  const [userIdFilter, setUserIdFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/sessions?stats=true', {
        headers: getSessionHeaders()
      })
      const data = await response.json()
      if (data.success) {
        setStats(data.stats)
      }
    } catch (err) {
      console.error('Error fetching session stats:', err)
    }
  }, [])

  const fetchSessions = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(userIdFilter && { userId: userIdFilter })
      })

      const response = await fetch(`/api/admin/sessions?${params}`, {
        headers: getSessionHeaders()
      })

      if (!response.ok) {
        const err = await response.json()
        throw new Error(err.error || 'Failed to fetch sessions')
      }

      const data = await response.json()
      setSessions(data.sessions || [])
      setTotalPages(data.pagination?.totalPages || 1)
    } catch (err: any) {
      setError(err.message)
      setSessions([])
    } finally {
      setLoading(false)
    }
  }, [page, statusFilter, userIdFilter])

  useEffect(() => {
    fetchStats()
    fetchSessions()
  }, [fetchStats, fetchSessions])

  const revokeSession = async (sessionId: string) => {
    if (!confirm('Are you sure you want to revoke this session? The user will be logged out.')) {
      return
    }

    try {
      const response = await fetch('/api/admin/sessions', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getSessionHeaders()
        },
        body: JSON.stringify({ sessionId })
      })

      const data = await response.json()
      if (data.success) {
        fetchSessions()
        fetchStats()
      } else {
        alert(data.error || 'Failed to revoke session')
      }
    } catch (err: any) {
      alert('Error revoking session: ' + err.message)
    }
  }

  const revokeAllUserSessions = async (userId: number, userEmail: string) => {
    if (!confirm(`Are you sure you want to revoke ALL sessions for ${userEmail}? They will be logged out from all devices.`)) {
      return
    }

    try {
      const response = await fetch(`/api/admin/sessions?all=true&userId=${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getSessionHeaders()
        },
        body: JSON.stringify({ userId, all: true })
      })

      const data = await response.json()
      if (data.success) {
        alert(`Revoked ${data.revokedCount} sessions for ${userEmail}`)
        fetchSessions()
        fetchStats()
      } else {
        alert(data.error || 'Failed to revoke sessions')
      }
    } catch (err: any) {
      alert('Error revoking sessions: ' + err.message)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString()
  }

  const getTimeSince = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    return `${Math.floor(hours / 24)}d ago`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-yellow-500">🔐 Session Manager</h2>
          <p className="text-white mt-1">
            Monitor and manage user sessions across all devices
          </p>
        </div>
        <button
          onClick={() => { fetchSessions(); fetchStats(); }}
          className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-3xl font-bold text-green-400">{stats.activeSessions}</div>
            <div className="text-sm text-white">Active Sessions</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-3xl font-bold text-blue-400">{stats.uniqueActiveUsers}</div>
            <div className="text-sm text-white">Active Users</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-3xl font-bold text-yellow-400">{stats.recentSessions}</div>
            <div className="text-sm text-white">Last 24 Hours</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-3xl font-bold text-purple-400">{stats.loginMethodCounts?.email || 0}</div>
            <div className="text-sm text-white">Email Logins</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-3xl font-bold text-cyan-400">{stats.loginMethodCounts?.telegram || 0}</div>
            <div className="text-sm text-white">Telegram Logins</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center bg-gray-800 rounded-lg p-4 border border-gray-700">
        <div>
          <label className="text-sm text-white block mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as any); setPage(1); }}
            className="bg-gray-700 text-white rounded px-3 py-2 border border-gray-600"
          >
            <option value="all">All</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive/Expired</option>
          </select>
        </div>
        <div>
          <label className="text-sm text-white block mb-1">User ID</label>
          <input
            type="text"
            value={userIdFilter}
            onChange={(e) => { setUserIdFilter(e.target.value); setPage(1); }}
            placeholder="Filter by user ID"
            className="bg-gray-700 text-white rounded px-3 py-2 border border-gray-600 w-40"
          />
        </div>
        {userIdFilter && (
          <button
            onClick={() => setUserIdFilter('')}
            className="mt-6 text-gray-400 hover:text-white"
          >
            ✕ Clear
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 rounded-lg p-4 text-red-300">
          ❌ {error}
        </div>
      )}

      {/* Sessions Table */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-sm text-white">User</th>
                <th className="px-4 py-3 text-left text-sm text-white">Session</th>
                <th className="px-4 py-3 text-left text-sm text-white">Login</th>
                <th className="px-4 py-3 text-left text-sm text-white">Last Activity</th>
                <th className="px-4 py-3 text-left text-sm text-white">Status</th>
                <th className="px-4 py-3 text-left text-sm text-white">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-white">
                    Loading sessions...
                  </td>
                </tr>
              ) : sessions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-white">
                    No sessions found
                  </td>
                </tr>
              ) : sessions.map((session) => (
                <tr key={session.session_id} className="hover:bg-gray-700/50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{session.user_full_name || 'Unknown'}</div>
                    <div className="text-sm text-white">@{session.user_username}</div>
                    <div className="text-sm text-gray-500">{session.user_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-mono text-sm text-gray-300">{session.session_id_short}</div>
                    <div className="text-sm text-gray-500">{session.ip_address}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className={`inline-flex items-center px-2 py-1 rounded text-sm ${
                      session.login_method === 'telegram' ? 'bg-blue-900/50 text-blue-300' : 'bg-green-900/50 text-green-300'
                    }`}>
                      {session.login_method === 'telegram' ? '📱 Telegram' : '📧 Email'}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">{formatDate(session.created_at)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-white">{getTimeSince(session.last_activity)}</div>
                    <div className="text-sm text-gray-500">Expires: {formatDate(session.expires_at)}</div>
                  </td>
                  <td className="px-4 py-3">
                    {session.is_active && !session.is_expired ? (
                      <span className="inline-flex items-center px-2 py-1 rounded text-sm bg-green-900/50 text-green-300">
                        🟢 Active
                      </span>
                    ) : session.is_expired ? (
                      <span className="inline-flex items-center px-2 py-1 rounded text-sm bg-yellow-900/50 text-yellow-300">
                        ⏰ Expired
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded text-sm bg-red-900/50 text-red-300">
                        🔴 Revoked
                      </span>
                    )}
                    {session.invalidation_reason && (
                      <div className="text-sm text-gray-500 mt-1">{session.invalidation_reason}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {session.is_active && !session.is_expired && (
                        <button
                          onClick={() => revokeSession(session.session_id)}
                          className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-500"
                          title="Revoke this session"
                        >
                          Revoke
                        </button>
                      )}
                      <button
                        onClick={() => revokeAllUserSessions(session.user_id, session.user_email)}
                        className="px-3 py-1 bg-orange-600 text-white rounded text-sm hover:bg-orange-500"
                        title="Revoke all sessions for this user"
                      >
                        Logout All
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center px-4 py-3 bg-gray-900 border-t border-gray-700">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50"
            >
              ← Previous
            </button>
            <span className="text-white">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default SessionManager
